import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import permissionsService from '../services/permissionsService';
import { getSectorConfig } from '../config/permissions';
import { Shield, Save, RotateCcw, CheckCircle, XCircle, AlertCircle, ExternalLink, Info } from 'lucide-react';
import ConfirmModal from './ConfirmModal';

const PermissionsManagement = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState(null);
  const [showBlockPermissionsInfo, setShowBlockPermissionsInfo] = useState(false);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [pendingRoleChange, setPendingRoleChange] = useState(null);

  // Buscar todas as permissões de todos os roles
  const { data: rolesData, isLoading: loadingRoles } = useQuery({
    queryKey: ['permissions', 'all'],
    queryFn: () => permissionsService.getAllRolesPermissions(),
    select: (data) => data.data || []
  });

  // Buscar permissões disponíveis (mapeamento)
  const { data: availablePermissionsData, isLoading: loadingAvailable } = useQuery({
    queryKey: ['permissions', 'available'],
    queryFn: () => permissionsService.getAvailablePermissions(),
    select: (data) => data.data || []
  });

  // Mutation para atualizar permissões
  const updateMutation = useMutation({
    mutationFn: ({ role, permissions }) => permissionsService.updateRolePermissions(role, permissions),
    onSuccess: (data, variables) => {
      toast.success(`Permissões de ${getSectorConfig(variables.role).name} atualizadas com sucesso!`);
      queryClient.invalidateQueries(['permissions', 'all']);
      queryClient.invalidateQueries(['permissions', 'role', variables.role]);
      setHasChanges(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar permissões');
    }
  });

  // Mutation para resetar permissões
  const resetMutation = useMutation({
    mutationFn: (role) => permissionsService.resetRolePermissions(role),
    onSuccess: (data, variables) => {
      toast.success(`Permissões de ${getSectorConfig(variables).name} resetadas para o padrão!`);
      queryClient.invalidateQueries(['permissions', 'all']);
      queryClient.invalidateQueries(['permissions', 'role', variables]);
      if (selectedRole === variables) {
        loadRolePermissions(variables);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao resetar permissões');
    }
  });

  // Carregar permissões de um role
  const loadRolePermissions = (role) => {
    const roleData = rolesData?.find(r => r.role === role);
    if (roleData) {
      setRolePermissions([...roleData.permissions]);
      setHasChanges(false);
    }
  };

  // Selecionar role
  const handleSelectRole = (role) => {
    if (hasChanges && selectedRole) {
      setPendingRoleChange(role);
      setShowDiscardConfirm(true);
      return;
    }
    setSelectedRole(role);
    loadRolePermissions(role);
  };

  const confirmDiscardChanges = () => {
    setShowDiscardConfirm(false);
    if (pendingRoleChange) {
      setSelectedRole(pendingRoleChange);
      loadRolePermissions(pendingRoleChange);
      setPendingRoleChange(null);
    }
  };

  const cancelDiscardChanges = () => {
    setShowDiscardConfirm(false);
    setPendingRoleChange(null);
  };

  // Toggle permissão
  const togglePermission = (permission) => {
    const index = rolePermissions.indexOf(permission);
    if (index > -1) {
      setRolePermissions(rolePermissions.filter(p => p !== permission));
    } else {
      setRolePermissions([...rolePermissions, permission]);
    }
    setHasChanges(true);
  };

  // Salvar alterações
  const handleSave = () => {
    if (!selectedRole) return;
    updateMutation.mutate({ role: selectedRole, permissions: rolePermissions });
  };

  // Resetar para padrão
  const handleReset = () => {
    if (!selectedRole) return;
    setShowResetConfirm(true);
  };

  const confirmReset = () => {
    setShowResetConfirm(false);
    resetMutation.mutate(selectedRole);
  };

  // Selecionar primeira role automaticamente
  useEffect(() => {
    if (rolesData && rolesData.length > 0 && !selectedRole) {
      handleSelectRole(rolesData[0].role);
    }
  }, [rolesData]);

  if (loadingRoles || loadingAvailable) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando permissões...</p>
        </div>
      </div>
    );
  }

  const roleConfig = selectedRole ? getSectorConfig(selectedRole) : null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gerenciamento de Permissões</h1>
                <p className="text-gray-600 mt-1">Controle o acesso de cada role às funcionalidades do sistema</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {hasChanges && (
                <div className="flex items-center space-x-2 text-orange-600">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">Alterações não salvas</span>
                </div>
              )}
              <div className="relative">
                <button
                  onClick={() => setShowBlockPermissionsInfo(!showBlockPermissionsInfo)}
                  className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  title="Informações sobre Bloqueio de Agenda"
                >
                  <Info className="w-4 h-4" />
                  <span className="text-sm">Bloqueio de Agenda</span>
                </button>
                {showBlockPermissionsInfo && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border p-4 z-50">
                    <h4 className="font-semibold text-gray-900 mb-2">Permissões de Bloqueio de Agenda</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      As permissões de bloqueio de agenda são <strong>separadas</strong> destas permissões do sistema.
                      Para gerenciar quem pode criar bloqueios de agenda, acesse a tela de Agendamentos.
                    </p>
                    <button
                      onClick={() => navigate('/agendamentos')}
                      className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>Ir para Agendamentos</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Lista de Roles */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Roles do Sistema</h2>
              <div className="space-y-2">
                {rolesData?.map((roleData) => {
                  const config = getSectorConfig(roleData.role);
                  const isSelected = selectedRole === roleData.role;
                  return (
                    <button
                      key={roleData.role}
                      onClick={() => handleSelectRole(roleData.role)}
                      className={`w-full text-left p-3 rounded-lg transition-all ${
                        isSelected
                          ? 'bg-blue-50 border-2 border-blue-500'
                          : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-xl">{config.icon}</span>
                          <div>
                            <div className="font-medium text-gray-900">{config.name}</div>
                            <div className="text-xs text-gray-500">{roleData.permissions.length} permissões</div>
                          </div>
                        </div>
                        {roleData.isCustom && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            Custom
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content - Permissões */}
          <div className="lg:col-span-3">
            {selectedRole && roleConfig ? (
              <div className="bg-white rounded-lg shadow-sm p-6">
                {/* Role Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b">
                  <div className="flex items-center space-x-3">
                    <span className="text-3xl">{roleConfig.icon}</span>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{roleConfig.name}</h2>
                      <p className="text-sm text-gray-600">{roleConfig.description}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleReset}
                      disabled={resetMutation.isLoading}
                      className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span>Resetar</span>
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={!hasChanges || updateMutation.isLoading}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      <span>Salvar Alterações</span>
                    </button>
                  </div>
                </div>

                {/* Permissões por Categoria */}
                <div className="space-y-6">
                  {availablePermissionsData?.map((category) => (
                    <div key={category.category} className="border rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">{category.category}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {category.permissions.map((permission) => {
                          const isChecked = rolePermissions.includes(permission.key);
                          return (
                            <label
                              key={permission.key}
                              className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all ${
                                isChecked
                                  ? 'bg-blue-50 border-2 border-blue-200'
                                  : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => togglePermission(permission.key)}
                                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{permission.description}</div>
                                <div className="text-xs text-gray-500 font-mono">{permission.key}</div>
                              </div>
                              {isChecked ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              ) : (
                                <XCircle className="w-5 h-5 text-gray-400" />
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Resumo */}
                <div className="mt-6 pt-6 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      Total de permissões ativas: <strong className="text-gray-900">{rolePermissions.length}</strong>
                    </span>
                    {rolePermissions.includes('all:full') && (
                      <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full font-medium">
                        Acesso Total ao Sistema
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Selecione um role para gerenciar suas permissões</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showDiscardConfirm}
        onClose={cancelDiscardChanges}
        onConfirm={confirmDiscardChanges}
        title="Descartar alterações?"
        message="Você tem alterações não salvas. Deseja descartá-las?"
        confirmText="Descartar"
        cancelText="Cancelar"
        type="warning"
      />

      <ConfirmModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={confirmReset}
        title="Resetar permissões?"
        message="Deseja resetar as permissões para o padrão? Esta ação não pode ser desfeita."
        confirmText="Resetar"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default PermissionsManagement;

